import { ICON_TYPES } from "@shared/common/constants";
import React, { ReactNode, useContext } from "react";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import CardSorters from "common/CardSorters/CardSorters";
import styled, { css, useTheme } from "styled-components";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  formatSignedPercent,
  MINUS,
} from "features/trades/NewLook/useMoneyFormat.ts";
import Card from "common/Boxes/Card";
import Flex from "common/Boxes/Flex";
import { GridCardData, LanguageNameSpaces } from "@shared/types";
import Col from "antd/es/col";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import Text from "common/Typography/Text";
import { formatDateRelative } from "@shared/utils/date";
import {
  getDistanceFromCurrentPrice,
  isTakeProfitOrderType,
} from "@shared/features/trades/common/utils";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import ChangeTag from "common/Tags/ChangeTag";
import { round } from "@shared/utils/numbers";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import useMedia from "utils/hooks/useMedia";
import { MAX_DRAWER_EXTENSION_SCREEN } from "common/constants";
import OrderNumber from "common/Numbering/OrderNumber";
import TradeChart from "features/trades/TradeDrawer/TradeChart";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const GridsTab = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const theme = useTheme();
  const isSmallScreen = useMedia(
    [`(max-width: ${MAX_DRAWER_EXTENSION_SCREEN})`],
    [true],
    false
  );
  const {
    symbol,
    exchange,
    gridCardsData,
    pricePrecision,
    amountPrecision,
    ordersData,
    getTradeExtraInfoActivitiesStatus,
    getTradeExtraInfoOrdersStatus,
    getTradeExtraInfoGridsStatus,
  } = useContext(TradeDetailsContext)!;
  const _currentPrice = ordersData?.data.current_price;
  const { socketPrice } = usePricesSocket(symbol, exchange);
  const currentPrice = socketPrice.value ?? _currentPrice;
  const columnSpans = [10, 7, 7];

  const getLastOrder = (gridData: GridCardData) => {
    const lastActivity = gridData.last_order_dict;
    if (!lastActivity) return <Text type="medium">N/A</Text>;
    return (
      <>
        <Text type="secondary">
          {isTakeProfitOrderType(lastActivity.type)
            ? t("orders.last_take_profit")
            : t("orders.last_entry")}
        </Text>
        <Text style={{ fontWeight: 500 }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {formatDateRelative(lastActivity.time, true, true, true)}
        </Text>
      </>
    );
  };

  const getPendingOrder = (gridData: GridCardData) => {
    if (!gridData.pending_order_dict || !currentPrice)
      return <Text type="medium">N/A</Text>;
    const distance = getDistanceFromCurrentPrice(currentPrice, gridData.price);
    const isTakeProfit = isTakeProfitOrderType(
      gridData.pending_order_dict.type
    );
    return (
      <>
        <Text type="secondary">
          {isTakeProfit
            ? t("orders.take_profit_title")
            : t("orders.entry_title")}
        </Text>
        <Text
          style={{
            color: isTakeProfit ? theme.colors.red : theme.colors.green,
            fontWeight: 500,
          }}
        >
          {distance}%
        </Text>
      </>
    );
  };

  const getSummaryItem = (
    key: string,
    value: ReactNode,
    addBorder?: boolean
  ) => {
    return (
      <SummaryItemWrapper $addBorder={addBorder}>
        <Text type="secondary">{key}</Text>
        <Text style={{ marginLeft: 8 }} as="div">
          {value}
        </Text>
      </SummaryItemWrapper>
    );
  };

  const getGridSummary = (gridData: GridCardData) => {
    const pnlAmount = (
      <ChangeTag
        prefix="$"
        value={round(gridData.realised_pnl_dict?.USD || 0, 3)}
        showArrow={false}
        style={{ marginLeft: 0 }}
        colorZeroValue={false}
      />
    );
    return (
      <GridSummaryWrapper>
        {getSummaryItem(
          t("grids.matched_orders"),
          gridData.num_of_filled_take_profits,
          true
        )}
        {getSummaryItem(t("grids.grid_pnl"), pnlAmount)}
      </GridSummaryWrapper>
    );
  };

  const getGridCard = (gridData: GridCardData) => {
    return (
      <CardWrapper key={gridData.id}>
        {getGridSummary(gridData)}
        <Flex>
          <Col span={columnSpans[0]}>
            <Flex $justifyContent="flex-start">
              <OrderNumber displayNumber={gridData.number} size="l" />
              <Flex
                $flexDirection="column"
                $alignItems="flex-start"
                $justifyContent="flex-start"
              >
                <TextWithPrecisionAndIcon
                  text={gridData.price}
                  iconType={ICON_TYPES.COIN}
                  coinOrExchangeName={ordersData?.data.price_coin}
                  precision={pricePrecision}
                  textStyle={{ color: theme.colors.primaryText }}
                />
                {gridData.number !== 1 && (
                  <TextWithPrecisionAndIcon
                    text={gridData.amount}
                    iconType={ICON_TYPES.COIN}
                    coinOrExchangeName={ordersData?.data.amount_coin}
                    precision={amountPrecision}
                    textStyle={{ color: theme.colors.mediumText }}
                  />
                )}
              </Flex>
            </Flex>
          </Col>
          <Col span={columnSpans[1]}>{getLastOrder(gridData)}</Col>
          <Col span={columnSpans[2]}>{getPendingOrder(gridData)}</Col>
        </Flex>
      </CardWrapper>
    );
  };

  const isLoading =
    getTradeExtraInfoOrdersStatus.isLoading ||
    getTradeExtraInfoActivitiesStatus.isLoading ||
    getTradeExtraInfoGridsStatus.isLoading;

  // New look: the grids as rows of one panel (micro column labels, hairline
  // separators), the grid's PnL a direction-coloured figure, not a pill.
  const getNewLookGridRow = (gridData: GridCardData) => {
    const tokens = getNewLookTokens(theme);
    const pnl = round(gridData.realised_pnl_dict?.USD || 0, 3);
    const lastActivity = gridData.last_order_dict;
    const pending = gridData.pending_order_dict;
    const distance =
      pending && currentPrice
        ? getDistanceFromCurrentPrice(currentPrice, gridData.price)
        : undefined;
    return (
      <GridRow key={gridData.id}>
        <GridLead>
          <OrderNumber displayNumber={gridData.number} size="l" />
          <GridCell>
            <GridFigure>
              <TextWithPrecisionAndIcon
                text={gridData.price}
                iconType={ICON_TYPES.COIN}
                coinOrExchangeName={ordersData?.data.price_coin}
                precision={pricePrecision}
              />
            </GridFigure>
            {gridData.number !== 1 && (
              <GridMeta>
                <TextWithPrecisionAndIcon
                  text={gridData.amount}
                  iconType={ICON_TYPES.COIN}
                  coinOrExchangeName={ordersData?.data.amount_coin}
                  precision={amountPrecision}
                />
              </GridMeta>
            )}
          </GridCell>
        </GridLead>
        <GridCell>
          {lastActivity ? (
            <>
              <GridText>
                {isTakeProfitOrderType(lastActivity.type)
                  ? t("orders.last_take_profit")
                  : t("orders.last_entry")}
              </GridText>
              <GridMeta>
                {formatDateRelative(lastActivity.time, true, true, true)}
              </GridMeta>
            </>
          ) : (
            <GridMeta>{t("card.not_available")}</GridMeta>
          )}
        </GridCell>
        <GridCell>
          {pending && distance !== undefined ? (
            <>
              <GridText>
                {isTakeProfitOrderType(pending.type)
                  ? t("orders.take_profit_title")
                  : t("orders.entry_title")}
              </GridText>
              <GridMeta>{formatSignedPercent(Number(distance))}</GridMeta>
            </>
          ) : (
            <GridMeta>{t("card.not_available")}</GridMeta>
          )}
        </GridCell>
        <GridCell $end={true}>
          <GridFigure style={{ color: directionColor(tokens, pnl) }}>
            {pnl > 0 ? "+" : pnl < 0 ? MINUS : ""}$
            {Math.abs(pnl).toLocaleString("en-US", {
              maximumFractionDigits: 3,
            })}
          </GridFigure>
          <GridMeta>
            {t("grids.matched_count", {
              count: gridData.num_of_filled_take_profits,
            })}
          </GridMeta>
        </GridCell>
      </GridRow>
    );
  };

  if (isLoading) {
    return (
      <LoadingSpinnerOverlay
        isLoading={true}
        spinnerMarginTop={50}
        hideContentWhileLoading={true}
      />
    );
  }

  if (theme.isNewLook) {
    return (
      <>
        {isSmallScreen && <TradeChart />}
        <GridsPanel style={{ marginTop: isSmallScreen ? 16 : 0 }}>
          <GridRow as="div" $head={true}>
            <span style={{ paddingLeft: 40 }}>{t("orders.price")}</span>
            <span>{t("orders.last_filled")}</span>
            <span>{t("orders.pending_order")}</span>
            <span style={{ textAlign: "right" }}>
              {t("grids.grid_pnl_column")}
            </span>
          </GridRow>
          {gridCardsData?.map(getNewLookGridRow)}
        </GridsPanel>
      </>
    );
  }

  return (
    <>
      {isSmallScreen && <TradeChart />}
      <StyledSorters
        style={{ marginTop: isSmallScreen ? 16 : 0 }}
        columnNames={[
          <div>
            <div>{t("orders.price")}</div>
            <div>({t("orders.amount")})</div>
          </div>,
          t("orders.last_filled"),
          t("orders.pending_order"),
        ]}
        columnSpans={columnSpans}
      />
      {gridCardsData?.map(getGridCard)}
    </>
  );
};

export default GridsTab;

const StyledSorters = styled(CardSorters)`
  padding: 1px 16px;
  margin: 16px 0;
`;
const CardWrapper = styled(Card)`
  padding: 12px 16px;
  background-color: ${(p) => p.theme.colors.cardBgColor};
  margin-bottom: 16px;
  ${(p) =>
    p.theme.isDarkMode &&
    `
        box-shadow: none;
    `};
`;
const GridSummaryWrapper = styled.div`
  padding-bottom: 12px;
  border-bottom: ${(p) => p.theme.borders.light};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
`;
const SummaryItemWrapper = styled.div<{ $addBorder?: boolean }>`
  flex: 1 1 100px;
  display: flex;
  justify-content: center;
  ${(p) => p.$addBorder && `border-right: ${p.theme.borders.light};`};
`;

const GridsPanel = styled.section`
  overflow: hidden;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
`;
const GridRow = styled.div<{ $head?: boolean }>`
  display: grid;
  grid-template-columns:
    minmax(0, 1.25fr) minmax(0, 1.25fr) minmax(0, 0.95fr)
    minmax(0, 0.7fr);
  column-gap: 12px;
  align-items: center;
  min-height: 56px;
  padding: 10px 16px;
  font-variant-numeric: tabular-nums;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  ${(p) =>
    p.$head &&
    css`
      min-height: 32px;
      padding-top: 8px;
      padding-bottom: 8px;
      ${newLookType.micro};
      color: ${getNewLookTokens(p.theme).muted};
    `}
`;
const GridLead = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;
const GridCell = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$end ? "flex-end" : "flex-start")};
  gap: 2px;
  min-width: 0;
`;
const GridFigure = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  * {
    font-size: inherit !important;
    font-weight: inherit !important;
  }
`;
const GridText = styled.span`
  ${newLookType.body};
  font-weight: 500;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const GridMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  * {
    font-size: inherit !important;
    color: inherit !important;
  }
`;
